import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { TOOL_CATEGORIES } from '@/shared/constants/category-colors';
import type { AgentToolCategoryOption } from '@/features/admin/components/AgentToolBasicInformation';

// Keyed off TOOL_CATEGORIES so adding a category to the product fails the build here instead of
// silently dropping out of the picker.
const CATEGORY_LABEL_KEYS = {
  plan: 'admin.agentCategoryOptions.plan',
  create: 'admin.agentCategoryOptions.create',
  scale: 'admin.agentCategoryOptions.scale',
  deliver: 'admin.agentCategoryOptions.deliver',
} as const satisfies Record<(typeof TOOL_CATEGORIES)[number], string>;

/**
 * The categories an admin may move an agent into. `support` is deliberately absent: no screen
 * offers it as a destination, so admitting it here would make the move one-way.
 *
 * The custom-agent form keeps its own wider list (it can set `support` outright, on an agent
 * that owns its metadata rather than overriding a registry value), so the two are not the same
 * set and should not be merged into one.
 */
export function useAgentCategoryOptions(): readonly AgentToolCategoryOption[] {
  const { t } = useTranslation('admin');

  return useMemo(
    () =>
      TOOL_CATEGORIES.map((value) => ({
        value,
        label: t(CATEGORY_LABEL_KEYS[value]),
      })) satisfies AgentToolCategoryOption[],
    [t],
  );
}
